import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Clock, ChartBar, ArrowRight, Sparkle } from "@phosphor-icons/react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
} from "recharts";

export default function ResultPage() {
    const { resultId } = useParams();
    const { state } = useLocation();
    const nav = useNavigate();
    const [result, setResult] = useState(state?.result || null);
    const [loading, setLoading] = useState(!state?.result);

    useEffect(() => {
        if (result) return;
        api.get(`/results/${resultId}`).then(r => setResult(r.data)).catch(() => nav("/results")).finally(() => setLoading(false));
    }, [resultId, result, nav]);

    if (loading || !result) {
        return <div className="min-h-screen grid place-items-center"><div className="dot-pulse"></div></div>;
    }

    const barData = Object.entries(result.topic_stats).map(([topic, s]) => ({
        topic,
        percent: Math.round(s.correct * 100 / (s.total || 1)),
        correct: s.correct,
        total: s.total,
    }));

    const radarData = Object.entries(result.topic_stats).map(([topic, s]) => ({
        topic: topic.length > 14 ? topic.slice(0, 12) + "…" : topic,
        Accuracy: Math.round(s.correct * 100 / (s.total || 1)),
        AvgTime: Math.min(100, s.avg_time),
    }));

    const percent = result.percent;
    const scoreColor = percent >= 75 ? "text-emerald-700" : percent >= 50 ? "text-gold-dark" : "text-oxblood";

    return (
        <div className="min-h-screen" data-testid="result-page">
            <Navbar />

            {/* Score hero */}
            <section className="mx-auto max-w-7xl px-6 py-14">
                <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Test complete</div>
                <div className="grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7">
                        <h1 className="font-serif text-6xl md:text-7xl leading-none text-ink">
                            You scored <span className={scoreColor}>{result.score}</span>
                            <span className="text-ink/30">/{result.total}</span>
                        </h1>
                        <div className="mt-4 text-lg text-ink/70">
                            <b>{result.course_name}</b> · {result.test_type === "mock" ? "Full Mock" : "Practice"} · Accuracy <b>{percent}%</b> · Time <b>{Math.round(result.total_time_sec/60)}m {result.total_time_sec % 60}s</b>
                        </div>
                    </div>
                    <div className="lg:col-span-5 grid grid-cols-3 gap-4">
                        <MetricCard label="Correct" value={result.score} icon={<CheckCircle size={18} className="text-emerald-700"/>} testid="metric-correct"/>
                        <MetricCard label="Wrong" value={result.total - result.score} icon={<XCircle size={18} className="text-oxblood"/>} testid="metric-wrong"/>
                        <MetricCard label="Avg time / Q" value={`${Math.round(result.total_time_sec / result.total)}s`} icon={<Clock size={18} className="text-ink"/>} testid="metric-avg-time"/>
                    </div>
                </div>
            </section>

            {/* AI Feedback */}
            <section className="mx-auto max-w-7xl px-6 pb-10">
                <div className="border border-ink/15 rounded-sm bg-ink text-bone p-8 md:p-12" data-testid="ai-feedback-card">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-4">
                        <Sparkle size={14} weight="fill"/> Coach's note · Claude Sonnet 4.6
                    </div>
                    <div className="font-serif text-2xl leading-relaxed whitespace-pre-wrap text-bone/95">
                        {result.ai_feedback}
                    </div>
                </div>
            </section>

            {/* Analytics */}
            <section className="mx-auto max-w-7xl px-6 pb-14 grid lg:grid-cols-2 gap-8">
                <div className="border border-ink/15 rounded-sm p-6 bg-bone" data-testid="topic-accuracy-chart">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-ink/50 mb-4">
                        <ChartBar size={14}/> Topic accuracy
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={barData} layout="vertical" margin={{ left: 0, right: 24 }}>
                                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#0A2540"/>
                                <YAxis type="category" dataKey="topic" width={110} tick={{ fontSize: 11 }} stroke="#0A2540"/>
                                <Tooltip contentStyle={{ fontSize: 12, background: "#F6F5F2", border: "1px solid #0A2540" }}
                                         formatter={(v, _, p) => [`${v}%  (${p.payload.correct}/${p.payload.total})`, "Accuracy"]}/>
                                <Bar dataKey="percent" radius={[0, 3, 3, 0]}>
                                    {barData.map((d, i) => (
                                        <Cell key={i} fill={d.percent >= 75 ? "#065F46" : d.percent >= 50 ? "#C5A059" : "#7F1D1D"}/>
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="border border-ink/15 rounded-sm p-6 bg-bone" data-testid="skill-radar-chart">
                    <div className="text-xs uppercase tracking-[0.3em] text-ink/50 mb-4">Skill radar</div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={radarData}>
                                <PolarGrid stroke="#0A254022"/>
                                <PolarAngleAxis dataKey="topic" tick={{ fontSize: 10, fill: "#0A2540" }}/>
                                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9 }}/>
                                <Radar name="Accuracy %" dataKey="Accuracy" stroke="#0A2540" fill="#0A2540" fillOpacity={0.35}/>
                                <Radar name="Avg time (s)" dataKey="AvgTime" stroke="#C5A059" fill="#C5A059" fillOpacity={0.25}/>
                                <Legend wrapperStyle={{ fontSize: 11 }}/>
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </section>

            {/* Question-by-question */}
            <section className="mx-auto max-w-7xl px-6 pb-24">
                <div className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Question review</div>
                <div className="space-y-4">
                    {result.results.map((r, i) => (
                        <div key={r.question_id} className={`border rounded-sm bg-bone ${r.is_correct ? "border-emerald-800/40" : "border-oxblood/40"}`} data-testid={`review-q-${i}`}>
                            <div className="p-6">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-widest text-ink/50">Q{i + 1} · {r.topic} · {r.time_spent_sec}s</div>
                                        <div className="font-serif text-lg text-ink mt-1">{r.question}</div>
                                    </div>
                                    {r.is_correct ? <CheckCircle size={22} weight="fill" className="text-emerald-700 shrink-0"/> : <XCircle size={22} weight="fill" className="text-oxblood shrink-0"/>}
                                </div>
                                <div className="mt-4 grid sm:grid-cols-2 gap-2">
                                    {r.options.map((o, oi) => {
                                        const isCorrect = oi === r.correct_index;
                                        const isChosen = oi === r.selected_index;
                                        return (
                                            <div key={oi} className={`p-3 border rounded-sm text-sm flex items-start gap-2 ${
                                                isCorrect ? "border-emerald-700 bg-emerald-50 text-ink" :
                                                isChosen ? "border-oxblood bg-oxblood/5 text-ink" :
                                                "border-ink/10 text-ink/70"
                                            }`}>
                                                <span className="w-5 h-5 grid place-items-center rounded-full border text-[10px] shrink-0 border-current">{String.fromCharCode(65 + oi)}</span>
                                                <span>{o}</span>
                                                {isCorrect && <span className="ml-auto text-[10px] uppercase tracking-widest text-emerald-700">Correct</span>}
                                                {isChosen && !isCorrect && <span className="ml-auto text-[10px] uppercase tracking-widest text-oxblood">Your pick</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-4 p-3 bg-parchment/60 rounded-sm text-sm text-ink/80 border border-ink/10">
                                    <span className="uppercase text-[10px] tracking-widest text-ink/50 mr-2">Explanation</span>
                                    {r.explanation}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-12 flex flex-wrap gap-3">
                    <Link to={`/courses/${result.course_id}`}>
                        <Button className="bg-ink text-bone hover:bg-ink/90 rounded-sm h-11" data-testid="btn-another-test">
                            Attempt another {result.test_type} <ArrowRight size={16} className="ml-2"/>
                        </Button>
                    </Link>
                    <Link to="/dashboard">
                        <Button variant="outline" className="rounded-sm h-11 border-ink/20 text-ink" data-testid="btn-back-dashboard">
                            Back to dashboard
                        </Button>
                    </Link>
                </div>
            </section>
            <Footer />
        </div>
    );
}

function MetricCard({ label, value, icon, testid }) {
    return (
        <div className="p-4 border border-ink/15 rounded-sm bg-bone" data-testid={testid}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-ink/50">{icon} {label}</div>
            <div className="font-serif text-3xl text-ink mt-1">{value}</div>
        </div>
    );
}
